import * as React from 'react';
import { Theme } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Text, RichText, Field } from '@sitecore-jss/sitecore-jss-nextjs';
import { StDropDown } from './ScrollList.style';
import { ComponentProps } from 'lib/component-props';
import Dropdown from '../shared/Dropdown/Dropdown';
import Tabs from '../shared/Tabs/Tabs';
import UiHeading from '../shared/Heading';
import UiText from '../shared/Typography/UiText';
import UiContainer from '../shared/Container/Container';
import { DEFAULT_PRIMARY_FONT_FAMILY } from 'theme/fontDefaults';

interface TabPanelProps {
  children?: React.ReactNode;
  ref?: any;
  index: number;
  value: number;
  heading: any;
}
interface ListItem {
  id: string;
  url: string;
  name: string;
  displayName: string;
  fields: {
    content: Field<string>;
    heading: Field<string>;
  };
}
type ScrollListProps = ComponentProps & {
  fields: {
    listofItems: ListItem[];
    content: Field<string>;
    heading: Field<string>;
  };
};

function debounce<T extends (...args: any[]) => void>(
  func: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
}

const ScrollList = (props: ScrollListProps): JSX.Element => {
  const [activeTabIndex, setActiveTabIndex] = React.useState(0);
  const theme = useTheme();
  const isLaptopView = useMediaQuery(theme.breakpoints.up('lg'));
  const scrollContainerRef = React.useRef<any>(null);
  type TabOption = {
    value: string;
  };
  type TabOptionArray = Array<TabOption> | [];
  const dropdownOptions: TabOptionArray =
    (props?.fields?.listofItems &&
      props?.fields?.listofItems?.map(({ fields }) => fields?.heading)) ||
    [];

  React.useEffect(() => {
    window.addEventListener('scroll', debounce(onScrollEventHandler, 100));
    return () => window.removeEventListener('scroll', onScrollEventHandler);
  }, []);

  function TabPanel(props: TabPanelProps) {
    const { children, index, heading, ...other } = props;

    return (
      <div
        role="tabpanel"
        id={heading?.value?.replaceAll(' ', '-')}
        aria-labelledby={`vertical-tab-${index}`}
        {...other}
      >
        <Box sx={{ p: isLaptopView ? 2 : 0 }}>{children}</Box>
      </div>
    );
  }

  function onclickHandler(_e: React.SyntheticEvent, val: number) {
    const nodes = scrollContainerRef.current?.children || [];
    const target = nodes[val];
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const rect = target.getBoundingClientRect();
    const spacer = isLaptopView ? 150 : 260;
    window.scrollTo({
      top: rect.top + scrollTop - spacer,
      behavior: 'smooth',
    });
  }

  const handleChange = (event: React.SyntheticEvent | any, newValue: number) => {
    setActiveTabIndex(newValue);
    event.preventDefault();
    event.stopPropagation();
    onclickHandler(event, newValue);
  };

  function onScrollEventHandler() {
    const sections = document.querySelectorAll('div[role="tabpanel"]');
    const scrollY = window.scrollY;

    // Now we loop through sections to get height, top and ID values for each
    let activeTab = 0;
    const topMarginBuffer = 250;

    for (let index = 0; index < sections.length; index++) {
      const current = sections[index] as HTMLDivElement;
      const sectionHeight = current?.offsetHeight;
      const sectionTop = current?.offsetTop - topMarginBuffer;
      activeTab = index;
      if (
        scrollY > sectionTop &&
        scrollY < sectionTop + sectionHeight &&
        activeTab !== activeTabIndex
      ) {
        setActiveTabIndex(activeTab);
        break;
      }
    }
  }

  const TabBody = React.useMemo(
    () => (
      <Grid
        item
        id="main-scroll-list"
        lg={8}
        sx={{
          backgroundColor: theme.palette.neutral0.main,
        }}
        ref={scrollContainerRef}
      >
        {props?.fields?.listofItems?.map(({ fields }, index) => {
          return (
            <TabPanel
              value={activeTabIndex}
              heading={fields?.heading}
              index={index}
              key={`${index + 1}`}
            >
              <UiHeading size="h3" isChildren>
                {`${index + 1}. `}
                <Text field={fields?.heading} />
              </UiHeading>
              <div style={{ padding: isLaptopView ? '8px 24px' : 0 }}>
                <UiText color="default">
                  <RichText field={fields?.content} />
                </UiText>
              </div>
            </TabPanel>
          );
        })}
      </Grid>
    ),
    [isLaptopView]
  );

  if (!props?.fields) return <></>;

  return (
    <UiContainer>
      <Grid
        container
        sx={{
          position: 'sticky',
          top: isLaptopView ? '64px' : '60px',
          zIndex: 1,
          background: 'white',
          alignItems: 'baseline',
          padding: '2rem 0 0',
        }}
      >
        <Grid item xs={12} lg={4}>
          <UiHeading size="h2" data-testid="page-title">
            {props?.fields?.heading}
          </UiHeading>
        </Grid>
        <Grid item xs={12} lg={8}>
          <UiText size="caption">
            <RichText field={props?.fields?.content} />
          </UiText>
        </Grid>
        {!isLaptopView && (
          <StDropDown
            item
            xs={12}
            sx={{
              background: 'white',
              paddingBottom: isLaptopView ? '32px' : '20px',
              display: { sm: 'block', lg: 'none' },
            }}
          >
            <Dropdown
              fullWidth={true}
              labelId="scroll-list-select-label"
              id="scroll-list-select"
              dataTestId="scroll-list-select"
              defaultValue={0}
              type="number"
              value={activeTabIndex}
              onChangeHandler={(e: any) => {
                const num = Number(e.target.value);
                handleChange(e, num);
              }}
              options={dropdownOptions}
              menuItemSxProps={{ whiteSpace: 'normal' }}
            />
          </StDropDown>
        )}
      </Grid>
      <Grid container>
        {isLaptopView && (
          <Grid
            item
            lg={4}
            sx={{
              position: 'sticky',
              top: '160px',
              height: 'calc(100vh - 154px)',
            }}
          >
            <Tabs
              value={activeTabIndex}
              type="text"
              orientation="vertical"
              onChangeHandler={handleChange}
              variant="scrollable"
              scrollButtons={'auto'}
              tabAltComponent={'a'}
              ScrollButtonComponent={() => {
                return null;
              }}
              ariaLabel="scrollable-tabs"
              tabListOptions={dropdownOptions}
              tabsContainerSxProps={{
                borderRight: 1,
                borderColor: 'divider',
                backgroundColor: '#fff',
                height: 'calc(100vh - 154px)',
                overflowY: 'scroll',
                scrollBehavior: 'smooth !important',

                '& .Mui-selected': {
                  color: '#ffffff !important',
                  backgroundColor: 'primary.main',
                  fontWeight: '600',
                },
                '& .MuiTabs-indicator': {
                  display: 'none',
                },
                '& svg': {
                  display: 'block',
                },
                '&::-webkit-scrollbar-track': {
                  display: 'none',
                },

                '& .MuiTabs-scroller .MuiTabs-flexContainerVertical': {
                  overflowY: 'scroll !important',
                  height: '100% !important',
                  '&::-webkit-scrollbar-track': {
                    display: 'none',
                  },
                },
              }}
              tabSxProps={{
                fontFamily: `${
                  theme?.palette?.primaryFontFamilyType || DEFAULT_PRIMARY_FONT_FAMILY
                }, sans-serif`,
                fontStyle: 'normal',
                fontWeight: 600,
                fontSize: '18px',
                lineHeight: '18px',

                color: (theme: Theme) => theme.bodyColor || theme.palette.neutral8.main,
                textAlign: 'left',
                maxWidth: '100%',
                position: 'relative',
                alignItems: 'flex-start',
                textTransform: 'none',
                left: 0,
                '&:hover': {
                  textDecoration: 'none',
                },
              }}
            />
          </Grid>
        )}
        {TabBody}
      </Grid>
    </UiContainer>
  );
};

export default ScrollList;
